import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';

// styles
import 'react-toastify/dist/ReactToastify.css';
import './css/pdf.css';

// components
import Modal from '../../components/Modal_3';
import Table from '../Table';

// tools
import { extractNumericQuantity, filterMaterialData } from '../../tools/manager';

// img
import DeleteIcon from '../../../assets/images/svg/delete-icon.svg';

// modèle de base pour une nouvelle intervention
const emptyMaterial = {
  idMaterial: '',
  nature: '',
  material: '',
  refName: '',
  quantity: '',       // numérique pour le back
  quantityLabel: '',  // ce que tape l’utilisateur ("3 u", "250 ml"…)
  pose: '',
  depot: '',
  surface: '',
  statut: false,
};

const EditPrefillWorkOrder = () => {
  const navigate = useNavigate();
  const { idCustomer, idworkorder } = useParams();

  // 🌱 state
  const [preInter, setPreInter] = useState(null);
  const [customer, setCustomer] = useState();
  const [typeInterventions, setTypeInterventions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [formError, setFormError] = useState('');
  const [newMaterial, setNewMaterial] = useState(emptyMaterial);
  const [selectedLisReferences, setSelectedLisReferences] = useState([]);
  const [modeAdd, setModeAdd] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [interSelectedDelete, setInterSelectedDelete] = useState(null);

  // 🌱 store actions
  const getListInterventionSettings = useStoreActions(
    actions => actions.middleware.OrdersMiddleware.getListInterventionSettings
  );
  const getListMaterialSettings = useStoreActions(
    actions => actions.middleware.OrdersMiddleware.getListMaterialSettings
  );
  const getCompany = useStoreActions(
    actions => actions.middleware.CompanyMiddleware.getCompany
  );
  const getPreCoupon = useStoreActions(
    actions => actions.middleware.CouponPreInterMiddleware.getPreCoupon
  );
  const updateListMaterial = useStoreActions(
    actions => actions.middleware.CouponPreInterMiddleware.updateListMaterial
  );
  const deleteInterventionCoupon = useStoreActions(
    actions => actions.middleware.CouponPreInterMiddleware.deleteInterventioncoupon
  );

  // ========= API CALLS =========

  const callListInterventionSettings = async () => {
    try {
      const res = await getListInterventionSettings();
      setTypeInterventions(res);
    } catch (error) {
      toast.error('❌ #1 Une erreur est survenue durant le chargement des données du bon');
      window.location.href = '/clients';
    }
  };

  const callListMaterialSettings = async () => {
    try {
      const res = await getListMaterialSettings();
    const data = res.filter(item => item.name !== 'Climatiseur');
      setMaterials(data);
    } catch (error) {
      toast.error('❌ #1 Une erreur est survenue durant la récupération des matériaux');
      window.location.href = '/clients';
    }
  };

  const callGetCompany = async () => {
    try {
      const res = await getCompany(idCustomer);
      setCustomer(res);
    } catch (error) {
      toast.error('❌ #1 Une erreur est survenue durant la récupération des données du client');
      window.location.href = '/clients';
    }
  };

  const callGetPreCoupon = async () => {
    try {
      const res = await getPreCoupon(idworkorder);
      setPreInter(res);
    } catch (error) {
      toast.error('❌ #1 Une erreur est survenue durant le chargement des données du bon');
    }
  };

  const callUpdateListMaterial = async data => {
    try {
      await updateListMaterial(data);
      setNewMaterial(emptyMaterial);
      setSelectedLisReferences([]);
      setModeAdd(false);
      await callGetPreCoupon();
    } catch (error) {
      toast.error('❌ #1 Une erreur est survenue durant la modification des données du bon');
    } finally {
      setInterSelectedDelete(null);
      setShowModal(false);
    }
  };

  const callDeleteCoupon = async () => {
    try {
      await deleteInterventionCoupon(idworkorder);
      navigate(-1);
    } catch (error) {
      toast.error('❌ #1 Une erreur est survenue durant la suppression du bon');
    }
  };

  // ========= HANDLERS =========

  // Ajout d’une nouvelle intervention
  const handleAddMaterial = e => {
    e.preventDefault();

    if (!modeAdd) {
      setModeAdd(true);
      return;
    }

    if (isOkForm()) {
      const numeric = extractNumericQuantity(newMaterial.quantityLabel);

      const materialToSave = {
        ...newMaterial,
        quantity: numeric === '' ? null : numeric,
      };

      const myListMaterials = [...(preInter?.materials || []), materialToSave];
      const newData = {
        id: idworkorder,
        materials: filterMaterialData(myListMaterials),
      };
      callUpdateListMaterial(newData);
    }
  };

  // Ouverture modal suppression d’une ligne
  const deleteInter = key => {
    setInterSelectedDelete(key);
    setShowModal(true);
  };

  // Suppression effective dans la liste
  const killInter = () => {
    const myListMaterials = [...(preInter?.materials || [])];
    myListMaterials.splice(interSelectedDelete, 1);
    const newData = {
      id: idworkorder,
      materials: filterMaterialData(myListMaterials),
    };
    callUpdateListMaterial(newData);
  };

  // Nature de l’intervention (champ libre + suggestions)
  const handleNatureChange = e => {
    const value = e.target.value;
    setNewMaterial(prev => ({ ...prev, nature: value }));
  };

  // Sélection du matériel
  const handleMaterialChange = e => {
    const id = e.target.value;
    const myMaterial = materials.find(el => el.id === id);
    if (!myMaterial) {
      setNewMaterial(prev => ({ ...prev, idMaterial: '', material: '', refName: '' }));
      setSelectedLisReferences([]);
      return;
    }

    setNewMaterial(prev => ({
      ...prev,
      idMaterial: id,
      material: myMaterial.name,
      refName: '',
    }));

    const rawRefs = myMaterial.references?.[0]?.reference || '';
    const refs = rawRefs
      .split(',')
      .map(r => r.trim())
      .filter(Boolean);

    setSelectedLisReferences(refs);
  };

  // Autres champs de la ligne (avec gestion spécifique de quantityLabel)
  const updateNewMaterialField = (field, value) => {
    if (field === 'quantityLabel') {
      const numeric = extractNumericQuantity(value);
      setNewMaterial(prev => ({
        ...prev,
        quantityLabel: value,
        quantity: numeric === '' ? '' : numeric,
      }));
      return;
    }

    setNewMaterial(prev => ({ ...prev, [field]: value }));
  };

  // Validation
  const isOkForm = () => {
    if (!newMaterial.nature.trim()) {
      setFormError("Défaut d'une nature d'intervention");
      return false;
    }
    if (!newMaterial.material.trim()) {
      setFormError("Défaut d'un nom de matériel");
      return false;
    }
    if (!newMaterial.refName.trim()) {
      setFormError("Défaut d'une référence");
      return false;
    }
    if (!newMaterial.quantityLabel?.trim()) {
      setFormError("Défaut d'une quantité (ex : 3, 10 u, 250 ml)");
      return false;
    }
    const numeric = extractNumericQuantity(newMaterial.quantityLabel);
    if (numeric === '' || Number(numeric) <= 0) {
      setFormError("Quantité invalide (ex : 3, 10 u, 250 ml)");
      return false;
    }
    return true;
  };

  // ========= EFFECTS =========

  // reset erreur quand newMaterial change
  useEffect(() => {
    setFormError('');
  }, [newMaterial]);

  // chargement initial
  useEffect(() => {
    callGetCompany();
    callListInterventionSettings();
    callListMaterialSettings();
    callGetPreCoupon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========= RENDER =========

  return (
    <section className="pt-14">
      {/* Infos entête */}
      <div className="flex flex-col lg:flex-row lg:space-x-8 mx-14">
        <div className="flex flex-col space-y-4">
          <label>Nom du client</label>
          <p className="w-fit rounded-lg placeholder:text-blue-admin p-4 border border-white-admin">
            {customer?.name}
          </p>
        </div>
        <div className="flex flex-col space-y-4">
          <label>BC CTM</label>
          <p className="w-fit rounded-lg placeholder:text-blue-admin p-4 border border-white-admin">
            {preInter?.ctm || ''}
          </p>
        </div>
        <div className="flex flex-col space-y-4">
          <label>Numéro du bon</label>
          <p className="w-fit rounded-lg placeholder:text-blue-admin p-4 border border-white-admin">
            {preInter?.numero || ''}
          </p>
        </div>
      </div>

      {/* Liste des interventions existantes */}
      <h5 className="font-semibold ml-14 mt-4">Etat des lieux des interventions</h5>

      {!preInter?.materials?.length && (
        <p className="ml-20 my-5 italic text-red-error">
          Vous n'avez aucune intervention liée à ce bon !
        </p>
      )}

      {!!preInter?.materials?.length && (
        <div className="mx-14 w-full mt-3">
          <Table>
            <thead>
              <tr>
                <th className="text-xs">Nature de l'intervention</th>
                <th className="text-xs">Nom du matériel</th>
                <th className="text-xs">Référence</th>
                <th className="text-xs">Surface</th>
                <th className="text-xs">Lieu de pose</th>
                <th className="text-xs">Lieu de dépose</th>
                <th className="text-xs">Quantité</th>
                <th className="text-xs">Commentaire</th>
                <th className="text-xs">Action</th>
              </tr>
            </thead>
            <tbody>
              {preInter.materials.map((material, index) => (
                <tr key={index}>
                  <td
                    className={`border ${
                      material?.statut ? 'border-blue-admin' : 'border-white-admin'
                    } pl-3 border-l-8`}
                  >
                    {material?.nature || '-'}
                  </td>
                  <td
                    className={`border ${
                      material?.statut ? 'border-blue-admin' : 'border-white-admin'
                    } pl-3 py-1`}
                  >
                    {material?.material || '-'}
                  </td>
                  <td
                    className={`border ${
                      material?.statut ? 'border-blue-admin' : 'border-white-admin'
                    } pl-3 py-1`}
                  >
                    {material?.refName || '-'}
                  </td>
                  <td
                    className={`border ${
                      material?.statut ? 'border-blue-admin' : 'border-white-admin'
                    } pl-3 py-1`}
                  >
                    {material?.surface || '-'}
                  </td>
                  <td
                    className={`border ${
                      material?.statut ? 'border-blue-admin' : 'border-white-admin'
                    } pl-3 py-1`}
                  >
                    {material?.pose || '-'}
                  </td>
                  <td
                    className={`border ${
                      material?.statut ? 'border-blue-admin' : 'border-white-admin'
                    } pl-3 py-1`}
                  >
                    {material?.depot || '-'}
                  </td>
                  <td
                    className={`border ${
                      material?.statut ? 'border-blue-admin' : 'border-white-admin'
                    } pl-3 py-1`}
                  >
                    {material?.quantityLabel || material?.quantity || '-'}
                  </td>
                  <td
                    className={`border ${
                      material?.statut ? 'border-blue-admin' : 'border-white-admin'
                    } pl-3 py-1`}
                  >
                    {material?.comments || '-'}
                  </td>
                  <td
                    className="border border-white pl-3 cursor-pointer animTrash py-1"
                    onClick={() => deleteInter(index)}
                  >
                    <img src={DeleteIcon} alt="delete icon" />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Ajout d’une nouvelle intervention */}
      <div className="mt-10 mx-14 overflow-x-auto">
        <h5 className="font-semibold mb-4">Ajouter une intervention</h5>

        {modeAdd && (
          <Table>
            <thead>
              <tr>
                <th>*Nature de l'intervention</th>
                <th>*Nom du matériel</th>
                <th>*Référence</th>
                <th>Surface</th>
                <th>Lieu de pose</th>
                <th>Lieu de dépose</th>
                <th>*Quantité</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                {/* Nature : champ libre + suggestions */}
                <td className="border border-white border-r-8">
                  <input
                    className="mt-3 w-[240px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin text-blue-admin inputFocus"
                    type="text"
                    placeholder="Nature de l'intervention"
                    list="nature-options"
                    value={newMaterial.nature}
                    onChange={handleNatureChange}
                  />
                  <datalist id="nature-options">
                    {typeInterventions.map(type => (
                      <option key={type.id} value={type.name} />
                    ))}
                  </datalist>
                </td>

                {/* Nom du matériel */}
                <td className="border border-white border-x-8 ">
                  <select
                    className="rounded-lg py-4 border border-white-admin text-blue-admin input mt-3 w-[234px]"
                    value={newMaterial.idMaterial || ''}
                    onChange={handleMaterialChange}
                  >
                    <option disabled value="">
                      {' '}
                      - Nom du matériel -{' '}
                    </option>
                    {materials.map(material => (
                      <option key={material.id} value={material.id}>
                        {material.name}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Référence */}
                <td className="border border-white border-x-8">
                  <select
                    className="rounded-lg py-4 border border-white-admin text-blue-admin input mt-3 w-[234px]"
                    value={newMaterial.refName}
                    onChange={e => updateNewMaterialField('refName', e.target.value)}
                    disabled={!selectedLisReferences.length}
                  >
                    <option disabled value="">
                      -
                    </option>
                    {selectedLisReferences.map((reference, index) => (
                      <option key={index} value={reference}>
                        {reference}
                      </option>
                    ))}
                  </select>
                </td>

                {/* Surface */}
                <td className="border border-x-8 border-white">
                  <input
                    className="rounded-lg py-4 border border-white-admin input mt-3"
                    type="number"
                    placeholder="-"
                    value={newMaterial.surface}
                    onChange={e => updateNewMaterialField('surface', e.target.value)}
                  />
                </td>

                {/* Lieu de pose */}
                <td className="border border-x-8 border-white">
                  <input
                    className="rounded-lg py-4 border border-white-admin input mt-3"
                    type="text"
                    placeholder="-"
                    value={newMaterial.pose}
                    onChange={e => updateNewMaterialField('pose', e.target.value)}
                  />
                </td>

                {/* Lieu de dépose */}
                <td className="border border-x-8 border-white">
                  <input
                    className="rounded-lg py-4 border border-white-admin input mt-3"
                    type="text"
                    placeholder="-"
                    value={newMaterial.depot}
                    onChange={e => updateNewMaterialField('depot', e.target.value)}
                  />
                </td>

                {/* Quantité : champ libre + parse */}
                <td className="border border-x-8 border-white">
                  <input
                    className="rounded-lg py-4 border border-white-admin input mt-3"
                    type="text"
                    placeholder="Ex : 3, 10 u, 250 ml"
                    value={newMaterial.quantityLabel}
                    onChange={e => updateNewMaterialField('quantityLabel', e.target.value)}
                  />
                </td>
              </tr>
            </tbody>
          </Table>
        )}

        <div className="mt-5">
          <button
            className={`w-[280px] py-3 text-sm ${
              modeAdd ? 'text-white bg-blue-admin' : 'text-blue-admin'
            } rounded-lg border border-white-admin mb-2`}
            onClick={handleAddMaterial}
          >
            + Ajouter une nouvelle intervention
          </button>
        </div>
      </div>

      {!!formError && (
        <p className="text-red-error text-end mt-5 mr-20 italic">{formError}</p>
      )}

      {/* Footer actions */}
      <div className="mt-16 py-4 flex justify-between items-center border-t border-white-admin">
        <button
          onClick={() => navigate(-1)}
          className="border rounded-lg cursor-pointer ml-14 border-white-admin px-5 py-3 text-blue-admin"
        >
          Retour
        </button>
        <button
          onClick={() => setShowModalDelete(true)}
          className="border rounded-lg cursor-pointer ml-14 bg-red-error mr-14 px-5 py-3 text-white"
        >
          Supprimer le bon
        </button>
      </div>

      {/* Modal suppression intervention */}
      <Modal
        showModal={showModal}
        title="Intervention"
        text="Voulez-vous vraiment supprimer cette intervention du bon ?"
        image={DeleteIcon}
      >
        <button
          className="px-4 py-3 text-white rounded-lg cursor-pointer bg-red-error w-full"
          onClick={killInter}
        >
          Supprimer
        </button>
        <button
          className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-blue-admin mt-1 w-full"
          onClick={() => setShowModal(false)}
        >
          Annuler
        </button>
      </Modal>

      {/* Modal suppression bon */}
      <Modal
        showModal={showModalDelete}
        title={`Bon d'intervention n°${preInter?.numero}`}
        text="Voulez-vous vraiment supprimer ce bon d'intervention ?"
        image={DeleteIcon}
      >
        <button
          className="px-4 py-3 text-white rounded-lg cursor-pointer bg-red-error w-full"
          onClick={callDeleteCoupon}
        >
          Supprimer
        </button>
        <button
          className="px-4 py-3 text-blue-admin rounded-lg cursor-pointer border border-blue-admin mt-1 w-full"
          onClick={() => setShowModalDelete(false)}
        >
          Annuler
        </button>
      </Modal>
    </section>
  );
};

export default EditPrefillWorkOrder;
